// @flow

import styled from "@emotion/styled";
import { ifProp } from "styled-tools";
import { rgba } from "polished";
import { SystemCSSProperties } from "@chakra-ui/react";

import { ChatElement } from "src/components/messages/Chat/styles";
import { AssistantTitle as StyledAssistantTitle } from "src/components/messages/Prompt/styles";
import * as colors from "src/styles/constants/colors";
import * as spacing from "src/styles/constants/spacing";
import * as size from "src/styles/constants/size";
import { HiddenInput } from "src/styles";
import { IconLabel } from "src/styles/box";

export const NotificationContainer = styled(ChatElement)`
  font-weight: 400;
`;

export const Common = styled.div`
  position: relative;
  background: ${rgba(colors.primary, 0.1)};
  display: inline-flex;
  align-items: baseline;
  padding: 0.4em 0.8em;
  gap: 24px;
  border-radius: 0.8em;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`;

export const CommonContent = styled.div`
  display: inline-block;
  width: fit-content;
  overflow: hidden;
  font-size: 14px;
`;

export const Comment = styled.div`
  display: flex;
  background: ${colors.brightLight};
  padding: 0.6em;
  margin: 0.4em 0 0.4em 0 !important;
  border-radius: 0.4em;
`;

export const FileOrEmailContainer = styled(ChatElement)`
  display: flex;
  position: relative;
  gap: ${ifProp("first", "8px")};
  > div {
    margin-top: 0;
    > div:nth-of-type(2) {
      max-width: calc(100% - 50px);
    }
  }
`;

export const FileOrEmailContent = styled.div`
  padding: 0.8em;
  display: flex;
  flex-direction: column;
  background: ${ifProp(
    "isCurrentUser",
    colors.currentUserHighlight,
    colors.grey0,
  )};
  margin-bottom: 1em;
  min-width: ${size.minChatWidth};
`;

export const EmailHeader = styled.div`
  display: flex;
`;

export const Comments = styled.div`
  margin: ${spacing.space_inset_default};
  cursor: text;
  max-width: 548px;
`;

export const FileContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${ifProp(
    "isCurrentUser",
    colors.currentUserHighlight,
    colors.grey0,
  )};
  cursor: pointer;
  min-width: 200px;
`;

export const FilePreviewContainer = styled.div`
  > div {
    max-width: 548px;
    max-height: 548px;
  }
  > div > img {
    max-width: 100%;
    margin: 0;
  }
`;

export const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  > div:first-of-type {
    font-weight: bold;
  }
`;

export const FileDetails = styled.div`
  display: flex;
  align-items: center;
  padding: ${(props) => (props.padding ? "16px 8px 8px" : "0")};
`;

export const HiddenImage = styled.img`
  width: 0px;
  height: 0px;
`;

export const EmailContainer = styled.div`
  background: ${colors.grey0};
  display: flex;
  padding: 0.8em;
`;

export const EmailIconContainer = styled.div`
  width: 34px;
`;

export const EmailContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const Header = styled.h4`
  margin: 0;
`;

export const EmailAddresses = styled.div`
  font-size: 11px;
  color: ${colors.grey5};
`;

export const EmailBody = styled.div`
  position: relative;
  margin-top: 0.5em;
  background: ${colors.brightLight};
  border-radius: 0.2em;
  padding: 0.4em;
  white-space: normal;

  > div:first-of-type {
    line-height: 1.5em;
    overflow-y: hidden;
    overflow-x: auto;
    max-height: ${ifProp({ expanded: false }, "25rem", "auto")};
    width: 100%;
    font-size: 13px;
    margin: 0;
  }
`;

export const EmailActions = styled.div`
  display: flex;
  gap: 1rem;
  min-width: 20rem;
  margin-right: 1rem;
`;

export const ActionButton = styled.div`
  color: ${colors.active};
  background-color: "transparent";
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;
  overflow: hidden;
  max-width: 220px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const IconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 5px;
  cursor: pointer;
  width: 24px;
  height: 24px;
`;

export const FileUpload = styled(HiddenInput)`
  div {
    width: 24px;
    height: 24px;
    cursor: pointer;
    position: absolute;
    z-index: 1;
    top: 0;
  }
`;

export const Files = styled(IconLabel)`
  display: flex;
  flex-flow: column;
  align-items: flex-start;

  > a {
    margin: ${spacing.space_s};
    color: ${colors.grey5};

    > span {
      line-height: 1.6;
      font-size: 1em;
    }
  }
`;

export const Download = styled.div`
  display: block;
  text-decoration: none;
  position: absolute;
  height: 40px;
  width: 40px;
  top: -40px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Status = styled.span`
  border-radius: 0.4em;
  padding: 0.2em 0.6em;
  margin-left: 0.8em;
  border: 1px solid;
  font-weight: bold;
  text-transform: uppercase;
`;

export const Complete = styled(Status)`
  border-color: ${colors.success};
  background: ${colors.currentUser};
  color: ${colors.success};
`;

export const Cancel = styled(Status)`
  border-color: ${colors.secondary};
  background: ${colors.secondary};
  color: ${colors.brightLight};
`;

export const Due = styled(Status)`
  border-color: ${colors.secondary};
  color: ${colors.secondary};
`;

export const ChecklistNotification = styled(ChatElement)`
  display: flex;
  position: relative;
`;

export const FieldName = styled.span`
  cursor: pointer;
  font-weight: 700;
  color: ${colors.active};
`;

export const ChecklistInfo = styled.div`
  font-size: ${size.fontSize.small};
  color: ${colors.secondary};
  span {
    cursor: pointer;
    font-weight: 700;
    color: ${colors.active};
  }
`;

export const StartComment = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  > div:first-of-type {
    font-size: 0.88rem;
  }
`;

export const ToggleText = styled.span`
  font-size: ${size.fontSize.base};
  line-height: 1rem;
  color: ${colors.blue};
  font-weight: bold;
  cursor: pointer;
  letter-spacing: 0.3px;
`;

export const MutedToggleText = styled(ToggleText)`
  cursor: initial;
  color: ${colors.inkLightest};
`;

export const AssistantTitle = styled(StyledAssistantTitle)`
  color: ${colors.blue};
  line-height: 20px;
`;

export const MessageText = styled.div`
  > span {
    line-height: 1rem;
    > p:empty {
      display: none;
    }
  }
  > span:nth-of-type(1) {
    margin-left: 0em;
    > p:empty {
      display: none;
    }
  }
  > span > p {
    margin: 0em;
    margin-right: 0.2em;
    display: inline-block;
  }
  > span {
    > span {
      font-weight: bold;
      > span {
        font-weight: bold;
      }
    }
  }
`;

export const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  font-size: ${size.fontSize.small};
  color: ${colors.inkLight};
`;

export const Modal: SystemCSSProperties = {
  overflowY: "scroll",
};

export const ModalHeader: SystemCSSProperties = {
  position: "sticky",
  top: "0",
  width: "100%",
  bg: colors.brightLight,
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

export const GroupMembersModalTitle: SystemCSSProperties = {
  color: colors.grey29,
  textTransform: "uppercase",
  fontSize: "10px",
  fontWeight: 500,
  lineHeight: "16px",
  letterSpacing: "1.5px",
  whiteSpace: "nowrap",
  minWidth: "280px",
  maxWidth: "280px",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const GroupMemberCard: SystemCSSProperties = {
  display: "flex",
  alignItems: "center",
  my: 3,
};

export const GroupModalCloseButton: SystemCSSProperties = {
  background: "white",
  outline: "none",
  border: "none",
  "*": {
    cursor: "pointer",
  },
};

export const MemberAvatar: SystemCSSProperties = {
  bg: colors.unifizeBlue,
  color: colors.brightLight,

  borderRadius: "4px",
  "& img": {
    borderRadius: "3px",
  },
};

export const MemberNameWrapper: SystemCSSProperties = {
  ml: 4,
  flexDirection: "column",
};

export const MemberName: SystemCSSProperties = {
  maxWidth: "9rem",
  my: 0,
  fontSize: "xs",
  lineHeight: "1rem",
};

export const MemberEmail: SystemCSSProperties = {
  my: 0,
  fontSize: "10px",
  lineHeight: "0.75rem",
  color: colors.grey5,
};

export const GroupTitle: SystemCSSProperties = {
  color: colors.blue500,
  cursor: "pointer",
  textDecoration: "underline",
};

export const NotificationHeaderTitle = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
